<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useLocale } from 'shared/composables/useLocale';
import { useMetaCampaignsFormatters } from '../composables/useMetaCampaignsFormatters';

const props = defineProps({
  campaign: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const { resolvedLocale } = useLocale();
const { brl } = useMetaCampaignsFormatters();

const leads = computed(() => props.campaign?.leads || []);

const formatDateTime = iso =>
  new Intl.DateTimeFormat(resolvedLocale.value, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));

const conversationHref = displayId =>
  router.resolve(
    accountScopedRoute('inbox_conversation', { conversation_id: displayId })
  ).href;

const goToConversation = lead => {
  router.push(
    accountScopedRoute('inbox_conversation', {
      conversation_id: lead.conversation_display_id,
    })
  );
};
</script>

<template>
  <div class="rounded-xl border border-n-strong bg-n-alpha-3 overflow-hidden">
    <div class="overflow-x-auto">
      <table class="w-full text-sm">
        <thead>
          <tr class="border-b border-n-weak text-xs text-n-slate-10">
            <th class="text-left font-medium px-4 py-2.5">
              {{ t('META_CAMPAIGNS.CAMPAIGN_DETAIL.LEADS.LEAD') }}
            </th>
            <th class="text-left font-medium px-4 py-2.5">
              {{ t('META_CAMPAIGNS.CAMPAIGN_DETAIL.LEADS.DATE') }}
            </th>
            <th class="text-left font-medium px-4 py-2.5">
              {{ t('META_CAMPAIGNS.CAMPAIGN_DETAIL.LEADS.ORIGIN') }}
            </th>
            <th class="text-right font-medium px-4 py-2.5">
              {{ t('META_CAMPAIGNS.CAMPAIGN_DETAIL.LEADS.SALE') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="lead in leads"
            :key="lead.conversation_id"
            class="border-b border-n-weak last:border-0 cursor-pointer hover:bg-n-alpha-2"
            @click="goToConversation(lead)"
          >
            <td class="px-4 py-2.5">
              <a
                :href="conversationHref(lead.conversation_display_id)"
                target="_blank"
                rel="noopener noreferrer"
                class="text-n-slate-12 font-medium hover:text-n-brand hover:underline"
                @click.stop
              >
                {{
                  lead.contact_name ||
                  lead.contact_phone_number ||
                  lead.contact_email ||
                  `#${lead.conversation_display_id}`
                }}
              </a>
            </td>
            <td class="px-4 py-2.5 text-n-slate-11">
              {{ formatDateTime(lead.created_at) }}
            </td>
            <td class="px-4 py-2.5">
              <span
                class="text-xs px-2 py-0.5 rounded-full bg-n-slate-3 text-n-slate-11"
              >
                {{
                  lead.source === 'manual'
                    ? t('META_CAMPAIGNS.ATTRIBUTION.SOURCE_MANUAL')
                    : t('META_CAMPAIGNS.ATTRIBUTION.SOURCE_AUTO')
                }}
              </span>
            </td>
            <td class="px-4 py-2.5 text-right">
              <span
                v-if="lead.sales_count"
                class="text-xs font-medium text-n-teal-11"
              >
                {{ brl(lead.revenue) }}
              </span>
              <span v-else class="text-xs text-n-slate-9">—</span>
            </td>
          </tr>
          <tr v-if="!leads.length">
            <td
              colspan="4"
              class="px-4 py-6 text-center text-sm text-n-slate-9"
            >
              {{ t('META_CAMPAIGNS.CAMPAIGN_DETAIL.LEADS.NO_DATA') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
